import {
  Alert,
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import * as chartUtils from "chartUtils";
import * as dates from "common/dates";
import Component from "components/Component";
import DocumentsIssued from "components/common/DocumentsIssued";
import Notes from "components/common/Notes";
import TextNote from "components/common/TextNote";
import Visible from "components/common/Visible";
import ProLinesRetentions from "components/inputs/claims/retentions/ProLinesRetentions";
import ExposureFlags from "components/inputs/flags/ExposureFlags";
import UWJudgment from "components/inputs/flags/UWJudgment";
import ProductLine from "components/inputs/program/ProductLine";
import ActiveLayerEditor from "components/layers/ActiveLayerEditor";
import ActiveLayerPremiumAdjustmentsEditor from "components/layers/ActiveLayerPremiumAdjustmentsEditor";
import ActiveLayerSelector from "components/layers/ActiveLayerSelector";
import BaseLayer from "components/pricing/layerPricing/BaseLayer";
import LayerPricingChart from "components/pricing/layerPricing/LayerPricingChart";
import PricingTable from "components/pricing/layerPricing/PricingTable";
import UpdateLossFrequency from "components/pricing/lossFrequency/UpdateLossFrequency";
import PercentageSublimits from "components/pricing/sublimits/PercentageSublimits";
import * as config from "config";
import * as labels from "config.labels";
import { LAYER_PRICING_COMPACT_LAYOUT } from "flags";
import { useState } from "react";
import { connect } from "react-redux";
import { useSelector } from "react-redux";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as featureFlagHooks from "store/hooks/featureFlagHooks";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

const LayerPricing = (props) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const [targetPerspective, setTargetPerspective] = useState(
    dates.ProratedPerspective.PRORATED
  );

  const resolvedProgram = useSelector(programSelectors.selectResolvedProgram);

  const {
    isOn: isShowingProRataAdjustments,
  } = featureFlagHooks.useFeatureFlag(
    "ui/showProRataAdjustmentsInLayerPricing",
    { default: true }
  );

  const { isOn: isCompactLayout } = featureFlagHooks.useFeatureFlag(
    LAYER_PRICING_COMPACT_LAYOUT,
    {
      default: false,
    }
  );

  const proRataFactor =
    isShowingProRataAdjustments && resolvedProgram?.proRataAdjustment
      ? resolvedProgram.proRataAdjustment / 100
      : 1;
  return (
    <>
      <Visible byTag={"pricing.layerPricing.updateLossFrequency"}>
        <UpdateLossFrequency isCompact />
      </Visible>
      <Visible
        byTag={"pricing.layerPricing.layerSelectorTop"}
        defaultVisible={false}
      >
        <Grid item xs={12}>
          <ActiveLayerSelector
            title={translateLabel(labels.LAYER_PRICING_LAYER_SELECTOR_TITLE, {
              default: undefined,
            })}
          />
        </Grid>
      </Visible>
      <Grid item xs={12} md={4}>
        <Grid container spacing={3} direction={"column"}>
          <Visible
            byTag={"pricing.layerPricing.productLine"}
            defaultVisible={false}
          >
            <Grid item>
              <ProductLine />
            </Grid>
          </Visible>
          <Visible byTag={"pricing.layerPricing.baseLayer"}>
            <BaseLayer />
          </Visible>
          <ActiveLayerEditor
            title={translateLabel(labels.ACTIVE_LAYER_PANEL_TITLE, {
              default: "Pricing Inputs",
            })}
            subtitle={translateLabel(labels.ACTIVE_LAYER_PANEL_SUBTITLE, {
              default: "The terms of the selected layer that is being priced.",
            })}
            error={props.layer?.hasError}
            targetPerspective={targetPerspective}
            proRataFactor={proRataFactor}
          />
          {!isCompactLayout && (
            <Visible
              byTag={"pricing.layerPricing.documentsIssued"}
              defaultVisible={true}
            >
              <DocumentsIssued layer={props.layer} />
            </Visible>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
        <Grid
          container
          spacing={config.GRID_SPACING}
          direction={"column"}
          sx={{ minWidth: 0 }}
        >
          <Visible
            byTag={"pricing.layerPricing.pricingResults"}
            defaultVisible={true}
          >
            <Component
              title={`${translateLabel(labels.PRICING_RESULTS_TITLE_PREFIX, {
                default: "Pricing Results",
              })} for $${chartUtils.tickFormatter(
                props.layer?.limit
              )} xs $${chartUtils.tickFormatter(
                props.layer?.attachment
              )} Layer`}
              options={
                proRataFactor !== 1 && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          targetPerspective ===
                          dates.ProratedPerspective.PRORATED
                        }
                        onChange={(e) =>
                          setTargetPerspective(
                            e.target.checked
                              ? dates.ProratedPerspective.PRORATED
                              : dates.ProratedPerspective.ANNUALIZED
                          )
                        }
                        color={"primary"}
                      />
                    }
                    label={
                      <Typography fontSize={"0.8rem"}>
                        {targetPerspective ===
                        dates.ProratedPerspective.PRORATED
                          ? "Pro Rated"
                          : "Annualized"}
                      </Typography>
                    }
                  />
                )
              }
            >
              <PricingTable
                targetPerspective={targetPerspective}
                proRataFactor={proRataFactor}
              />
            </Component>
          </Visible>
          <Visible byTag={"layerPricing.judgments"} defaultVisible={false}>
            <Grid item>
              <Stack direction={"column"} spacing={config.GRID_SPACING}>
                <Alert severity={"info"}>
                  {translateLabel(labels.LAYER_PRICING_UW_JUDGMENTS_ALERT, {
                    default:
                      "NOTE - Judgments set here are applied to all layers.",
                  })}
                </Alert>
                <UWJudgment />
              </Stack>
            </Grid>
          </Visible>
          <Visible
            byTag={"pricing.layerPricing.explanation"}
            defaultVisible={true}
          >
            <TextNote
              title={translateLabel(labels.LAYER_PRICING_JUSTIFICATION_TITLE, {
                default: "UW Selected Justification",
              })}
              noteKey={`pricing.layerPricing.${props.layer?.id}.explanation`}
              hasEditButton={true}
            />
          </Visible>
          {isCompactLayout && (
            <Visible
              byTag={"pricing.layerPricing.documentsIssued"}
              defaultVisible={true}
            >
              <DocumentsIssued layer={props.layer} />
            </Visible>
          )}
          {isCompactLayout && (
            <Visible
              byTag={"pricing.layerPricing.layerSelectorBottom"}
              defaultVisible={true}
            >
              <Grid item xs={12} sx={{ minWidth: 0, maxWidth: "100%" }}>
                <Box
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflowX: "auto",
                  }}
                >
                  <ActiveLayerSelector
                    title={translateLabel(
                      labels.LAYER_PRICING_LAYER_SELECTOR_TITLE,
                      { default: undefined }
                    )}
                    compactColumns
                  />
                </Box>
              </Grid>
            </Visible>
          )}
          {isCompactLayout && (
            <Visible byTag={"pricing.layerPricing.premiumAdjustments"}>
              <Grid item>
                <ActiveLayerPremiumAdjustmentsEditor />
              </Grid>
            </Visible>
          )}
          {isCompactLayout && (
            <Notes update={props.updateNote} value={props.note} />
          )}
          <Visible
            byTag={"pricing.layerPricing.percentage-sublimits"}
            defaultVisible={false}
          >
            <PercentageSublimits />
          </Visible>

          <Visible
            byTag={"pricing.layerPricing.proLineRetentions"}
            defaultVisible={false}
          >
            <ProLinesRetentions />
          </Visible>
          <Visible byTag={"pricing.layerPricing.chart"} defaultVisible={true}>
            <LayerPricingChart
              targetPerspective={targetPerspective}
              proRataFactor={proRataFactor}
            />
          </Visible>
          <Visible
            byTag={"pricing.layerPricing.exposureFlags"}
            defaultVisible={false}
          >
            <Grid item>
              <ExposureFlags />
            </Grid>
          </Visible>
        </Grid>
      </Grid>
      {!isCompactLayout && (
        <Visible
          byTag={"pricing.layerPricing.layerSelectorBottom"}
          defaultVisible={true}
        >
          <Grid item xs={12}>
            <ActiveLayerSelector
              title={translateLabel(labels.LAYER_PRICING_LAYER_SELECTOR_TITLE, {
                default: undefined,
              })}
            />
          </Grid>
        </Visible>
      )}
      {!isCompactLayout && (
        <Visible byTag={"pricing.layerPricing.premiumAdjustments"}>
          <Grid item xs={12}>
            <ActiveLayerPremiumAdjustmentsEditor />
          </Grid>
        </Visible>
      )}
      {!isCompactLayout && (
        <Notes update={props.updateNote} value={props.note} />
      )}
    </>
  );
};

export const mapDispatchToProps = {
  updateNote: pricingActions.updateLayerPricingNote,
};

export const mapStateToProps = (state) => {
  return {
    layer: pricingSelectors.getActiveLayer(state),
    note: state.pricing.note || "",
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LayerPricing);
